import type { ScopeFilterState } from './scopeGroupFilters';
import type { ResolvedPeriod } from './scopeKpi';

/**
 * How a Custom group was built ("snapshot + refresh"): membership is the saved
 * id list; the recipe remembers the filters so they can be re-run later and
 * the difference previewed before saving. It is saved with the group on the
 * server (opsCommand/saveScopeGroup), which keeps it as it is sent.
 */
export type ScopeGroupRecipe = {
  v: 1;
  state: ScopeFilterState;
  /** KPI rule id → the dates it was evaluated over. */
  resolvedPeriods: Record<string, ResolvedPeriod>;
  /** ISO timestamp of the evaluation. */
  evaluatedAt: string;
  matchedCount: number;
  /** Hand edits on top of the filter matches, re-applied on re-run when kept. */
  manual: { added: string[]; removed: string[] };
  /** Detects membership edited elsewhere (e.g. Topbar) since the snapshot. */
  memberHash: string;
};

function isRecipe(value: unknown): value is ScopeGroupRecipe {
  if (!value || typeof value !== 'object') return false;
  const r = value as Partial<ScopeGroupRecipe>;
  return (
    r.v === 1 &&
    !!r.state &&
    Array.isArray(r.state.rules) &&
    Array.isArray(r.state.combinators) &&
    typeof r.evaluatedAt === 'string' &&
    !!r.manual &&
    Array.isArray(r.manual.added) &&
    Array.isArray(r.manual.removed)
  );
}

/** A group's recipe as the server returned it, if it is one this client can re-run. */
export function recipeOf(raw: unknown): ScopeGroupRecipe | null {
  return isRecipe(raw) ? raw : null;
}

/** Order-independent FNV-1a fingerprint of a member list. */
export function hashMembers(ids: Iterable<string>): string {
  let h = 0x811c9dc5;
  for (const id of [...new Set(ids)].sort()) {
    for (let i = 0; i < id.length; i++) {
      h ^= id.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    h ^= 0x2c; // separator so ["ab","c"] ≠ ["a","bc"]
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

export function diffMembership(
  saved: Iterable<string>,
  next: Iterable<string>,
): { added: string[]; removed: string[] } {
  const before = new Set(saved);
  const after = new Set(next);
  return {
    added: [...after].filter(id => !before.has(id)),
    removed: [...before].filter(id => !after.has(id)),
  };
}

export function buildScopeGroupRecipe({
  state,
  resolvedByRule,
  matchedIds,
  selectedIds,
  now = new Date(),
}: {
  state: ScopeFilterState;
  resolvedByRule: Map<string, ResolvedPeriod>;
  matchedIds: Iterable<string>;
  selectedIds: Iterable<string>;
  now?: Date;
}): ScopeGroupRecipe {
  const matched = new Set(matchedIds);
  const selected = new Set(selectedIds);
  const resolvedPeriods: Record<string, ResolvedPeriod> = {};
  for (const rule of state.rules) {
    const resolved = resolvedByRule.get(rule.id);
    if (resolved) resolvedPeriods[rule.id] = resolved;
  }
  return {
    v: 1,
    state,
    resolvedPeriods,
    evaluatedAt: now.toISOString(),
    matchedCount: matched.size,
    manual: {
      added: [...selected].filter(id => !matched.has(id)),
      removed: [...matched].filter(id => !selected.has(id)),
    },
    memberHash: hashMembers(selected),
  };
}

/** Membership after a re-run: fresh matches, optionally keeping hand edits. */
export function rerunMembership(
  matches: Iterable<string>,
  recipe: Pick<ScopeGroupRecipe, 'manual'> | null,
  keepManual: boolean,
): Set<string> {
  const next = new Set(matches);
  if (keepManual && recipe) {
    for (const id of recipe.manual.removed) next.delete(id);
    for (const id of recipe.manual.added) next.add(id);
  }
  return next;
}

export function membershipEditedSince(
  recipe: Pick<ScopeGroupRecipe, 'memberHash'>,
  currentIds: Iterable<string>,
): boolean {
  return recipe.memberHash !== hashMembers(currentIds);
}
